import { useCallback, useEffect, useState } from 'react';
import type { AppliesTo, Product } from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { usePms, usePmsScope, type PmsBundle } from './PmsProvider.js';
import { createdElsewhereFr, useAskEtablissement } from './CreateTarget.js';
import {
  buildProductDraft,
  describeProduct,
  emptyProduct,
  productToForm,
  type ProductForm,
} from './catalogueDraft.js';

// « Prestations » — the catalogue the booking form sells from.
//
// It did not exist until now, and that absence was load-bearing: `pms_product`
// held two rows in production (the taxe de séjour tariffs seeded by 0085) and
// nothing could add a third, so the booking form's Prestations block rendered
// nothing, no extra could be billed, and D3's « matched where the name maps »
// branch had nothing to match against.
//
// ── Its own component, mounted in Configuration ────────────────────────────
//
// Not folded into `Configuration.tsx`, which is about the PHYSICAL units — 42
// rooms, their beds and their views. A catalogue is a different subject that
// happens to share a screen, and that file is already long.
//
// ── The tax block is set apart, and that is the design ─────────────────────
//
// A taxe de séjour tariff is fixed by the communauté de communes' délibération.
// It is editable — the operator receives that text, and a tariff that could only
// be changed by a migration means the wrong figure is charged until a developer
// is free (it changed on 2026-08-14 and will change again). But it is not a
// price like breakfast, so it does not sit in the same list wearing the same
// clothes.

export function Catalogue() {
  const pms = usePms();
  const canEdit = useCapability(CAPABILITIES.PMS_RATE_WRITE);
  const [products, setProducts] = useState<Product[] | null>(null);
  const [form, setForm] = useState<ProductForm | null>(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  // U2: a NEW prestation belongs to no existing row, so its établissement is
  // asked when the form opens. An edit stays with the product it edits, which
  // is listed here because it belongs to the établissement on screen.
  const { ask, picker } = useAskEtablissement();
  const { current } = usePmsScope();
  const [newIn, setNewIn] = useState<PmsBundle | null>(null);
  const [notice, setNotice] = useState('');

  const startNew = async () => {
    setNotice('');
    const target = await ask('Nouvelle prestation');
    if (target === null) return;
    setNewIn(target);
    setForm(emptyProduct());
  };

  const load = useCallback(async () => {
    const r = await pms.reference.products();
    if (!r.ok) {
      setErr(r.error.message);
      return;
    }
    setProducts(r.value);
  }, [pms]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (form === null) return;
    const built = buildProductDraft(form);
    if (!built.ok) {
      setErr(built.error);
      return;
    }
    const isNew = form.id === null;
    if (isNew && newIn === null) return;
    setSaving(true);
    setErr('');
    const r = await (isNew && newIn ? newIn.services : pms).reference.saveProduct(built.draft);
    setSaving(false);
    if (!r.ok) {
      setErr(r.error.message);
      return;
    }
    if (isNew && newIn) setNotice(createdElsewhereFr(newIn, current) ?? '');
    setForm(null);
    setNewIn(null);
    await load();
  };

  const toggleActive = async (p: Product) => {
    // Never a delete: pms_charge and pms_ota_service both name a product, so a
    // billed one cannot be removed — and a past folio must keep reading
    // « Demi-pension » long after it stops being sold.
    if (
      p.active &&
      !window.confirm(
        `Désactiver « ${p.labelFr} » ?\n\nIl disparaîtra du formulaire de réservation. Les factures passées le montrent toujours.`,
      )
    ) {
      return;
    }
    setErr('');
    const r = await pms.reference.setProductActive(p.id, !p.active);
    if (!r.ok) setErr(r.error.message);
    await load();
  };

  const sellable = (products ?? []).filter((p) => !p.isTax);
  const taxes = (products ?? []).filter((p) => p.isTax);

  return (
    <div className="pms-catalogue">
      <div className="pms-head">
        <h2 className="section-title">Prestations</h2>
        <span className="pms-hint">
          Ce que le formulaire de réservation peut facturer en plus des nuitées.{' '}
          {canEdit ? 'Cliquez une ligne pour la modifier.' : 'Lecture seule.'}
        </span>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}

      {products === null && <p className="pms-empty">Chargement…</p>}

      {products !== null && sellable.length === 0 && form === null && (
        <p className="pms-empty">
          Aucune prestation. Tant que le catalogue est vide, aucun extra ne peut être facturé et les prestations
          vendues par les OTA restent en attente de rapprochement.
        </p>
      )}

      {sellable.length > 0 && (
        <table className="pms-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Libellé</th>
              <th>Tarif</th>
              <th>Application</th>
              <th>État</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sellable.map((p) => (
              <tr key={p.id} className={p.active ? '' : 'is-inactive'}>
                <td>{p.code}</td>
                <td>
                  {p.emoji ? `${p.emoji} ` : ''}
                  {p.labelFr}
                  {p.descriptionFr && <div className="pms-sub">{p.descriptionFr}</div>}
                </td>
                <td className="pms-num">
                  {describeProduct(p)}
                  {p.profileBand != null && <div className="pms-sub">fixé par le profil tarifaire</div>}
                </td>
                <td>{p.autoApply ? 'Automatique' : 'Sur demande'}</td>
                <td>{p.active ? 'Active' : 'Retirée'}</td>
                <td className="pms-actions">
                  {canEdit && (
                    <>
                      <button type="button" className="pms-mini" onClick={() => setForm(productToForm(p))}>
                        ✎
                      </button>
                      <button type="button" className="pms-mini" onClick={() => void toggleActive(p)}>
                        {p.active ? 'Désactiver' : 'Réactiver'}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {notice && <p className="pms-hint" role="status">{notice}</p>}

      {canEdit && form === null && (
        <div className="pms-room-add">
          <button type="button" className="auth-btn is-ghost" onClick={() => void startNew()}>
            + Ajouter une prestation
          </button>
        </div>
      )}

      {picker}

      {form !== null && (
        <ProductFields
          form={form}
          saving={saving}
          onChange={(patch) => setForm((f) => (f === null ? f : { ...f, ...patch }))}
          onSave={() => void save()}
          onCancel={() => {
            setForm(null);
            setNewIn(null);
            setErr('');
          }}
        />
      )}

      {/* ── La taxe de séjour ─────────────────────────────────────────────
          Its own block, with the warning, because it is not a price we set. */}
      {taxes.length > 0 && (
        <div className="pms-catalogue-tax">
          <h3>Taxe de séjour</h3>
          <p className="pms-hint">
            ⚠ Fixée par délibération de la communauté de communes — ce n’est pas un tarif que l’hôtel choisit. Ne la
            modifiez qu’avec le texte sous les yeux. Les séjours déjà facturés ne changent pas.
          </p>
          <table className="pms-table">
            <tbody>
              {taxes.map((p) => (
                <tr key={p.id} className={p.active ? '' : 'is-inactive'}>
                  <td>{p.labelFr}</td>
                  <td className="pms-num">
                    {(p.priceCents / 100).toFixed(2).replace('.', ',')} € / adulte / nuit
                  </td>
                  <td>{p.active ? 'Active' : 'Retirée'}</td>
                  <td className="pms-actions">
                    {canEdit && (
                      <button type="button" className="pms-mini" onClick={() => setForm(productToForm(p))}>
                        ✎
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface ProductFieldsProps {
  form: ProductForm;
  saving: boolean;
  onChange: (patch: Partial<ProductForm>) => void;
  onSave: () => void;
  onCancel: () => void;
}

const APPLIES_TO_LABELS: Record<AppliesTo, string> = {
  arrival: "Une fois, à l'arrivée",
  departure: 'Une fois, au départ',
  each_night: 'Chaque nuit',
  each_morning: 'Chaque matin',
};

function ProductFields({ form, saving, onChange, onSave, onCancel }: ProductFieldsProps) {
  return (
    <section className="pms-form">
      <h3>
        {form.id === null ? 'Nouvelle prestation' : `Modifier « ${form.labelFr || form.code} »`}
        {form.isTax && ' — taxe de séjour'}
      </h3>

      {form.isTax && (
        <p className="pms-hint">
          ⚠ Tarif légal. Il s’applique par adulte et par nuit et ne porte pas de TVA — ces trois points ne sont pas
          modifiables.
        </p>
      )}

      <div className="pms-form-grid">
        <label className="pms-field">
          <span>Code *</span>
          {/* Disabled on an existing product: it is the key charges were
              written against, so renaming it would orphan them. */}
          <input
            value={form.code}
            disabled={form.id !== null}
            placeholder="PDJ"
            onChange={(e) => onChange({ code: e.target.value.toUpperCase() })}
          />
        </label>
        <label className="pms-field">
          <span>Libellé *</span>
          <input
            value={form.labelFr}
            placeholder="Petit-déjeuner"
            onChange={(e) => onChange({ labelFr: e.target.value })}
          />
        </label>
        <label className="pms-field">
          <span>Emoji</span>
          <input value={form.emoji} placeholder="🥐" onChange={(e) => onChange({ emoji: e.target.value })} />
        </label>
        <label className="pms-field pms-field-wide">
          <span>Description</span>
          <input
            value={form.descriptionFr}
            onChange={(e) => onChange({ descriptionFr: e.target.value })}
          />
        </label>
        <label className="pms-field">
          <span>Prix (€) *</span>
          <input
            inputMode="decimal"
            placeholder="18,00"
            value={form.priceEuros}
            disabled={form.priceFromProfile}
            onChange={(e) => onChange({ priceEuros: e.target.value })}
          />
          {form.priceFromProfile && (
            <span className="pms-sub">Fixé par le profil tarifaire — Tarifs › Profil tarifaire, repas enfants.</span>
          )}
        </label>

        {!form.isTax && (
          <>
            <label className="pms-field">
              <span>TVA (%) *</span>
              <input
                inputMode="decimal"
                placeholder="10"
                value={form.tvaPercent}
                onChange={(e) => onChange({ tvaPercent: e.target.value })}
              />
            </label>
            <label className="pms-field">
              <span>Application</span>
              <select
                value={form.appliesTo}
                onChange={(e) => onChange({ appliesTo: e.target.value as AppliesTo })}
              >
                {(Object.keys(APPLIES_TO_LABELS) as AppliesTo[]).map((a) => (
                  <option key={a} value={a}>
                    {APPLIES_TO_LABELS[a]}
                  </option>
                ))}
              </select>
            </label>
            <label className="pms-field">
              <span>Nature</span>
              <select
                value={form.kind}
                onChange={(e) => onChange({ kind: e.target.value as Product['kind'] })}
              >
                <option value="service">Service</option>
                <option value="produit">Produit</option>
              </select>
            </label>
            <label className="pms-field">
              <span>Par personne</span>
              <input
                type="checkbox"
                checked={form.perPerson}
                onChange={(e) => onChange({ perPerson: e.target.checked })}
              />
            </label>
            <label className="pms-field">
              <span>Quantité modifiable</span>
              <input
                type="checkbox"
                checked={form.manualQty}
                onChange={(e) => onChange({ manualQty: e.target.checked })}
              />
            </label>
          </>
        )}

        <label className="pms-field">
          <span>Appliquer automatiquement</span>
          <input
            type="checkbox"
            checked={form.autoApply}
            onChange={(e) => onChange({ autoApply: e.target.checked })}
          />
        </label>
        <label className="pms-field">
          <span>Ordre d’affichage</span>
          <input
            inputMode="numeric"
            value={form.sortOrder}
            onChange={(e) => onChange({ sortOrder: e.target.value })}
          />
        </label>
      </div>

      {form.autoApply && (
        <p className="pms-hint">
          Facturé sur chaque séjour sans que la réception ait à le cocher — et non modifiable depuis le formulaire de
          réservation.
        </p>
      )}

      <div className="pms-form-actions">
        <button type="button" className="auth-btn is-ghost" onClick={onCancel} disabled={saving}>
          Annuler
        </button>
        <button type="button" className="auth-btn" onClick={onSave} disabled={saving}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </section>
  );
}
